import { buildPanelButton, setPanelButtonLabel } from './requestPanelButton'

const normalize = (value) => value.toString().toLowerCase().trim()

const triggerChange = (input) => {
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
}

// Formie 4 dispatches its events on the mount target, which is either the `<form>` itself
// or a wrapper element carrying `data-formie-form`.
const getEventForm = (event) => {
    const target = event?.target

    if (!target) {
        return null
    }

    if (target.matches?.('form')) {
        return target
    }

    return target.querySelector?.('form[data-formie-form]') || null
}

// The server reports what it did with the images through these statuses; see
// `modules/watishetwaard/services/AnalysisState.php` and `docs/aanvraagformulier-flows.md`.
const aiMessages = {
    proposed:
        'Je foto’s zijn gewijzigd, dus maakten we een nieuwe beschrijving. Je eigen tekst staat er nog — kies wat je wil bewaren.',
    failed: 'De automatische analyse lukte niet. Probeer opnieuw, of beschrijf je stuk hierboven zelf.'
}

// Shown once the server stops accepting retries, so the message never promises something the
// button can no longer deliver.
const aiExhaustedMessage =
    'De automatische analyse lukte niet. Beschrijf je stuk hierboven zelf.'

const retryLabel = 'Opnieuw proberen'

const getDescriptionField = (form) =>
    form?.querySelector('[data-formie-field-handle="customDesc"]')

const readAiPayload = (submitData) =>
    Array.isArray(submitData)
        ? (submitData.find((entry) => entry?.ai)?.ai ?? null)
        : null

const removeAiPanel = (field) => {
    field?.querySelector('[data-request-ai-panel]')?.remove()
}

const setDescription = (field, description) => {
    const textarea = field?.querySelector('textarea')

    if (!textarea || !description) {
        return
    }

    textarea.value = description
    triggerChange(textarea)
}

// Asks the module to run the analysis again on the images already attached to the submission.
// `submissionUid` is the hidden input Formie's own client writes into the form after every
// submit — a random UUID, so it identifies the submission without exposing a countable ID.
const retryAnalysis = async (form) => {
    const config = form?.querySelector('[data-request-endpoints]')

    if (!config?.dataset.retryUrl) {
        return null
    }

    const body = new FormData()
    const csrfName = config.dataset.csrfName
    const csrfInput = csrfName
        ? form.querySelector(`input[name="${csrfName}"]`)
        : null

    body.append(
        'handle',
        form.querySelector('input[name="handle"]')?.value ?? ''
    )
    body.append(
        'submissionUid',
        form.querySelector('input[name="submissionUid"]')?.value ?? ''
    )

    if (csrfInput) {
        body.append(csrfName, csrfInput.value)
    }

    try {
        const response = await fetch(config.dataset.retryUrl, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body
        })

        return response.ok ? await response.json() : null
    } catch {
        return null
    }
}

const renderAiPanel = (form, field, payload) => {
    removeAiPanel(field)

    const panel = document.createElement('div')
    const message = document.createElement('p')
    const actions = document.createElement('div')

    const isFailed = payload.status === 'failed'
    const isExhausted = isFailed && payload.canRetry === false

    panel.dataset.requestAiPanel = 'true'
    panel.className = `request-form__ai-panel${isFailed ? ' request-form__ai-panel--error' : ''}`
    panel.setAttribute('role', isFailed ? 'alert' : 'status')

    message.className = 'request-form__ai-message'
    message.textContent = isExhausted
        ? aiExhaustedMessage
        : (aiMessages[payload.status] ?? '')
    panel.appendChild(message)

    actions.className = 'request-form__ai-actions'

    // Never overwrite something the visitor wrote without asking — that is the whole point of
    // the `proposed` status. Untouched AI text is replaced server-side without a prompt.
    if (payload.status === 'proposed' && payload.description) {
        const replace = buildPanelButton(
            'Gebruik nieuwe beschrijving',
            'primary'
        )
        const keep = buildPanelButton('Mijn tekst behouden', 'ghost')

        replace.addEventListener('click', () => {
            setDescription(field, payload.description)
            removeAiPanel(field)
        })

        keep.addEventListener('click', () => removeAiPanel(field))

        actions.append(replace, keep)
    }

    if (isFailed && !isExhausted) {
        const retry = buildPanelButton(retryLabel, 'error')

        retry.addEventListener('click', async () => {
            retry.disabled = true
            setPanelButtonLabel(retry, 'Bezig…')

            const result = await retryAnalysis(form)

            if (!result) {
                retry.disabled = false
                setPanelButtonLabel(retry, retryLabel)
                return
            }

            applyAiResult(form, result)
        })

        actions.appendChild(retry)
    }

    if (actions.children.length) {
        panel.appendChild(actions)
    }

    field.appendChild(panel)
}

// Formie renders every page up front and never re-renders, so a description set server-side
// during the image step never reaches the textarea on its own. The module hands it back through
// Formie's `submitData` channel instead; the retry endpoint answers in the same shape.
const applyAiResult = (form, payload) => {
    const field = getDescriptionField(form)

    if (!payload || !field?.querySelector('textarea')) {
        return
    }

    if (payload.status === 'applied') {
        setDescription(field, payload.description)
        removeAiPanel(field)
        return
    }

    // Same images as last time, or nothing to analyse — leave the field exactly as it is.
    if (payload.status === 'unchanged' || payload.status === 'skipped') {
        removeAiPanel(field)
        return
    }

    renderAiPanel(form, field, payload)
}

let requestPageTabsReady = false
let requestAlertTimeoutReady = false
let requestFieldErrorsReady = false

const requiredMessage = 'Verplicht'

const normalizeRequiredErrors = (scope = document) => {
    scope
        .querySelectorAll('[data-formie-field-error], .form-field-error-badge')
        .forEach((error) => {
            const text = normalize(error.textContent || '')

            if (
                text.includes('mag niet leeg') ||
                text.includes('is verplicht') ||
                text.includes('cannot be blank')
            ) {
                error.textContent = requiredMessage
            }
        })
}

const setRequiredMessages = (scope = document) => {
    scope
        .querySelectorAll(
            '[data-custom-cat-field], [data-custom-artist-field], [data-custom-state-field], [data-custom-origin-field]'
        )
        .forEach((customField) => {
            const fieldWrapper = customField.parentElement

            fieldWrapper
                ?.querySelectorAll(
                    'input[required]:not([type="hidden"]), select[required], textarea[required]'
                )
                .forEach((input) => {
                    input.dataset.requiredMessage = requiredMessage
                })
        })
}

const syncRequestPageTabs = (form, nextPageId) => {
    if (!form || !nextPageId) {
        return
    }

    form.querySelectorAll('[data-request-tab-anchor]').forEach((tab) => {
        const isActive = tab.dataset.formiePageId === nextPageId.toString()
        const number = tab.querySelector('[data-tabs-number]')
        const mobileNumber = number?.querySelector('.inline-block.lg\\:hidden')

        tab.classList.toggle('lg:opacity-50', !isActive)
        number?.classList.toggle('max-lg:bg-brown-500/35', !isActive)
        number?.classList.toggle('max-lg:!size-4', !isActive)

        if (mobileNumber) {
            // Step number, not the page ID — page IDs are not sequential.
            const step = Number(tab.dataset.requestPageIndex ?? 0) + 1

            mobileNumber.textContent = isActive ? String(step) : ''
        }
    })
}

const getPageIndex = (form, pageId) => {
    const tab = form?.querySelector(
        `[data-request-tab-anchor][data-formie-page-id="${pageId}"]`
    )

    return tab ? Number(tab.dataset.requestPageIndex ?? 0) : null
}

const getFurthestPageIndex = (form) =>
    Number(form?.dataset.requestFurthestPage ?? 0)

const setFurthestPageIndex = (form, index) => {
    if (!form || !Number.isInteger(index)) {
        return
    }

    form.dataset.requestFurthestPage = String(
        Math.max(getFurthestPageIndex(form), index)
    )
}

// Jumping *forward* is what produces half-filled submissions: the tab links hit Formie's
// `set-page` action, which neither validates nor saves anything. Steps the visitor already
// completed stay reachable, so correcting something afterwards keeps working.
const lockRequestPageTabs = (form) => {
    if (!form) {
        return
    }

    const furthest = getFurthestPageIndex(form)

    form.querySelectorAll('[data-request-tab-anchor]').forEach((tab) => {
        const isLocked = Number(tab.dataset.requestPageIndex ?? 0) > furthest

        tab.toggleAttribute('data-request-tab-locked', isLocked)
        tab.setAttribute('aria-disabled', isLocked ? 'true' : 'false')
        tab.tabIndex = isLocked ? -1 : 0
    })
}

// Capture phase: Formie binds its own navigation handler to `[data-formie-tab-link]`, so a
// locked tab has to be intercepted before that listener ever sees the click.
const initRequestTabGuard = (form) => {
    if (!form || form.dataset.requestTabGuardReady === 'true') {
        return
    }

    form.dataset.requestTabGuardReady = 'true'

    form.addEventListener(
        'click',
        (event) => {
            const tab = event.target.closest?.('[data-request-tab-anchor]')

            if (tab?.hasAttribute('data-request-tab-locked')) {
                event.preventDefault()
                event.stopImmediatePropagation()
            }
        },
        true
    )
}

const initRequestPageTabs = (scope = document) => {
    scope.querySelectorAll('form[data-formie-form]').forEach((form) => {
        const currentPage = form.querySelector(
            '[data-formie-page]:not([data-formie-page-hidden])'
        )

        initRequestTabGuard(form)

        if (!currentPage?.dataset.formiePageId) {
            return
        }

        // A reload or a return through Barba lands on the page the visitor legitimately
        // reached, so that page is the floor for what stays unlocked.
        setFurthestPageIndex(
            form,
            getPageIndex(form, currentPage.dataset.formiePageId)
        )

        syncRequestPageTabs(form, currentPage.dataset.formiePageId)
        lockRequestPageTabs(form)
    })

    if (requestPageTabsReady) {
        return
    }

    requestPageTabsReady = true

    document.addEventListener('formie:page:navigate:after', (event) => {
        const form = getEventForm(event) || event.target
        const pageId = event.detail?.pageId

        setFurthestPageIndex(form, getPageIndex(form, pageId))
        syncRequestPageTabs(form, pageId)
        lockRequestPageTabs(form)
    })
}

const removeErrorAlertAfterDelay = (alert) => {
    if (!alert || alert.dataset.requestAlertTimeout) {
        return
    }

    alert.dataset.requestAlertTimeout = 'true'

    window.setTimeout(() => {
        alert.remove()
    }, 4000)
}

// Formie 4 renders its flash messages *inside* the `<form>` (`form/messages-top`),
// where Formie 3 put them next to it inside the old `formWrapper`.
const hideErrorAlert = (form) => {
    if (!form) {
        return
    }

    removeErrorAlertAfterDelay(
        form.querySelector('[data-formie-message-error]')
    )
}

const initRequestAlertObserver = (form) => {
    if (!form || form.dataset.requestAlertObserverReady === 'true') {
        return
    }

    form.dataset.requestAlertObserverReady = 'true'

    const observer = new MutationObserver(() => {
        hideErrorAlert(form)
    })

    observer.observe(form, {
        childList: true,
        subtree: true
    })
}

const initRequestAlertTimeout = (scope = document) => {
    scope.querySelectorAll('form[data-formie-form]').forEach((form) => {
        initRequestAlertObserver(form)
        hideErrorAlert(form)
    })

    if (requestAlertTimeoutReady) {
        return
    }

    requestAlertTimeoutReady = true

    document.addEventListener('formie:submit:result', (event) => {
        applyAiResult(
            getEventForm(event),
            readAiPayload(event.detail?.submitData)
        )

        if (event.detail?.ok) {
            return
        }

        window.setTimeout(() => hideErrorAlert(getEventForm(event)), 50)
    })

    document.addEventListener('formie:mount:after', (event) => {
        initRequestAlertObserver(getEventForm(event))
    })
}

// A failed final submit can carry errors from pages the visitor is no longer on, because the
// form is only submitted from the last one. The module reports the first page holding an error
// as `errorPageId`; we jump back to it so the visitor lands on the field that blocks them.
//
// Done by clicking Formie's own tab link rather than switching pages ourselves: that handler
// only toggles `data-formie-page-hidden` and persists the page server-side, so the error markup
// Formie just rendered stays untouched.
const navigateToErrorPage = (form, submitData) => {
    if (!form || !Array.isArray(submitData)) {
        return
    }

    const pageId = submitData.find((entry) => entry?.errorPageId)?.errorPageId

    if (!pageId) {
        return
    }

    const currentPage = form.querySelector(
        '[data-formie-page]:not([data-formie-page-hidden])'
    )

    if (currentPage?.getAttribute('data-formie-page-id') === String(pageId)) {
        return
    }

    form.querySelector(
        `[data-formie-tab-link][data-formie-page-id="${pageId}"]`
    )?.click()
}

const initRequestFieldErrors = (scope = document) => {
    setRequiredMessages(scope)
    normalizeRequiredErrors(scope)

    if (requestFieldErrorsReady) {
        return
    }

    requestFieldErrorsReady = true

    document.addEventListener('formie:submit:result', (event) => {
        if (event.detail?.ok) {
            return
        }

        navigateToErrorPage(getEventForm(event), event.detail?.submitData)

        normalizeRequiredErrors(event.target)
        window.requestAnimationFrame(() =>
            normalizeRequiredErrors(event.target)
        )
    })

    document.addEventListener('formie:mount:after', (event) => {
        const form = getEventForm(event)

        if (form) {
            setRequiredMessages(form)
            normalizeRequiredErrors(form)
        }

        if (!form || form.dataset.requestFieldErrorsObserverReady === 'true') {
            return
        }

        form.dataset.requestFieldErrorsObserverReady = 'true'

        const observer = new MutationObserver(() => {
            normalizeRequiredErrors(form)
        })

        observer.observe(form, {
            childList: true,
            subtree: true
        })
    })
}

const initCheckboxList = ({
    scope,
    fieldSelector,
    readyKey,
    searchSelector,
    optionSelector,
    optionTitleKey,
    checkboxSelector,
    badgeSelector,
    clearSelector
}) => {
    scope.querySelectorAll(fieldSelector).forEach((customField) => {
        if (customField.dataset[readyKey]) {
            return
        }

        customField.dataset[readyKey] = 'true'

        const fieldWrapper = customField.parentElement
        const searchInput = customField.querySelector(searchSelector)
        const options = customField.querySelectorAll(optionSelector)
        const customCheckboxes = customField.querySelectorAll(checkboxSelector)
        const formieCheckboxes = fieldWrapper.querySelectorAll(
            `input[type="checkbox"]:not(${checkboxSelector})`
        )
        const noItemsFound = customField.querySelector('[data-no-items-found]')
        const listFade = customField.querySelector('[data-custom-list-fade]')
        const scrollList = customField.querySelector('[data-lenis-prevent]')
        let isClearing = false

        const setListFadeVisible = (isVisible) => {
            if (!listFade) {
                return
            }

            listFade.classList.toggle('opacity-100', isVisible)
            listFade.classList.toggle('opacity-0', !isVisible)
            listFade.setAttribute('aria-hidden', 'true')
        }

        const updateListFade = () => {
            if (!scrollList) {
                setListFadeVisible(false)
                return
            }

            const hasNoResults = noItemsFound && !noItemsFound.hidden
            const hasOverflow =
                scrollList.scrollHeight > scrollList.clientHeight + 1
            const isAtBottom =
                scrollList.scrollTop + scrollList.clientHeight >=
                scrollList.scrollHeight - 1

            setListFadeVisible(!hasNoResults && hasOverflow && !isAtBottom)
        }

        const findFormieCheckbox = (value) => {
            return Array.from(formieCheckboxes).find(
                (checkbox) => checkbox.value === value
            )
        }

        const findCustomCheckbox = (value) => {
            return Array.from(customCheckboxes).find(
                (checkbox) => checkbox.value === value
            )
        }

        const updateOptionVisibility = () => {
            const query = normalize(searchInput?.value || '')
            let visibleCount = 0

            options.forEach((option, index) => {
                const title = normalize(option.dataset[optionTitleKey] || '')
                const checkbox = option.querySelector(checkboxSelector)
                const isSelected = checkbox?.checked

                option.hidden = query && !isSelected && !title.includes(query)
                option.style.order =
                    query && isSelected ? '-1' : index.toString()

                if (!option.hidden) {
                    visibleCount += 1
                }
            })

            if (noItemsFound) {
                noItemsFound.hidden = !query || visibleCount > 0
            }

            if (scrollList) {
                scrollList.style.overflowY =
                    query && visibleCount === 0 ? 'hidden' : ''
            }

            if (query && visibleCount === 0) {
                setListFadeVisible(false)
            }

            requestAnimationFrame(updateListFade)
        }

        const updateSelectedBadge = () => {
            if (!badgeSelector) {
                return
            }

            const selectedBadge = document.querySelector(badgeSelector)
            const selectedCount = selectedBadge?.querySelector(
                '[data-count], [data-number]'
            )
            const count = Array.from(customCheckboxes).filter(
                (checkbox) => checkbox.checked
            ).length

            if (!selectedBadge) {
                return
            }

            if (count === 0) {
                selectedBadge.classList.add('opacity-0')
                selectedBadge.classList.remove('opacity-100')
                return
            }

            if (selectedCount) {
                selectedCount.textContent = count.toString()
            }

            selectedBadge.classList.remove('opacity-0')
            selectedBadge.classList.add('opacity-100')
        }

        customCheckboxes.forEach((customCheckbox) => {
            const formieCheckbox = findFormieCheckbox(customCheckbox.value)
            const option = customCheckbox.closest(optionSelector)

            if (!formieCheckbox) {
                return
            }

            customCheckbox.checked = formieCheckbox.checked

            option?.addEventListener('click', (event) => {
                event.preventDefault()

                customCheckbox.checked = !customCheckbox.checked
                triggerChange(customCheckbox)
            })

            customCheckbox.addEventListener('change', () => {
                formieCheckbox.checked = customCheckbox.checked
                triggerChange(formieCheckbox)
                updateOptionVisibility()
                updateSelectedBadge()
            })
        })

        formieCheckboxes.forEach((formieCheckbox) => {
            formieCheckbox.addEventListener('change', () => {
                const customCheckbox = findCustomCheckbox(formieCheckbox.value)

                if (customCheckbox) {
                    customCheckbox.checked = formieCheckbox.checked

                    if (!isClearing) {
                        updateOptionVisibility()
                        updateSelectedBadge()
                    }
                }
            })
        })

        const clearButton = clearSelector
            ? scope.querySelector(clearSelector) ||
              document.querySelector(clearSelector)
            : null

        if (clearButton) {
            clearButton.addEventListener('click', () => {
                isClearing = true

                customCheckboxes.forEach((customCheckbox) => {
                    const formieCheckbox = findFormieCheckbox(
                        customCheckbox.value
                    )

                    customCheckbox.checked = false

                    if (formieCheckbox && formieCheckbox.checked) {
                        formieCheckbox.checked = false
                        triggerChange(formieCheckbox)
                    }
                })

                isClearing = false

                updateOptionVisibility()
                updateSelectedBadge()
            })
        }

        searchInput?.addEventListener('input', updateOptionVisibility)
        scrollList?.addEventListener('scroll', updateListFade, {
            passive: true
        })

        if (scrollList && 'ResizeObserver' in window) {
            const observer = new ResizeObserver(updateListFade)
            observer.observe(scrollList)
        }

        window.addEventListener('resize', updateListFade)
        updateOptionVisibility()
        updateSelectedBadge()
        updateListFade()
    })
}

const initRadioCards = ({ scope, fieldSelector, readyKey, radioSelector }) => {
    scope.querySelectorAll(fieldSelector).forEach((customField) => {
        if (customField.dataset[readyKey]) {
            return
        }

        customField.dataset[readyKey] = 'true'

        const fieldWrapper = customField.parentElement
        const customRadios = customField.querySelectorAll(radioSelector)
        const formieRadios = fieldWrapper.querySelectorAll(
            `input[type="radio"]:not(${radioSelector})`
        )

        const findFormieRadio = (value) => {
            return Array.from(formieRadios).find(
                (radio) => radio.value === value
            )
        }

        const findCustomRadio = (value) => {
            return Array.from(customRadios).find(
                (radio) => radio.value === value
            )
        }

        customRadios.forEach((customRadio) => {
            const formieRadio = findFormieRadio(customRadio.value)

            if (!formieRadio) {
                return
            }

            customRadio.checked = formieRadio.checked

            customRadio.addEventListener('change', () => {
                if (!customRadio.checked) {
                    return
                }

                formieRadio.checked = true
                triggerChange(formieRadio)
            })
        })

        formieRadios.forEach((formieRadio) => {
            formieRadio.addEventListener('change', () => {
                const customRadio = findCustomRadio(formieRadio.value)

                if (customRadio) {
                    customRadio.checked = formieRadio.checked
                }
            })
        })
    })
}

const initSellToggle = (scope) => {
    scope
        .querySelectorAll('[data-custom-sell-field]')
        .forEach((customField) => {
            if (customField.dataset.customSellReady) {
                return
            }

            customField.dataset.customSellReady = 'true'

            const fieldWrapper = customField.parentElement
            const customCheckbox = customField.querySelector(
                '[data-custom-sell-checkbox]'
            )
            const formieCheckbox = fieldWrapper.querySelector(
                'input[type="checkbox"]:not([data-custom-sell-checkbox])'
            )

            if (!customCheckbox || !formieCheckbox) {
                return
            }

            customCheckbox.checked = formieCheckbox.checked

            customCheckbox.addEventListener('change', () => {
                formieCheckbox.checked = customCheckbox.checked
                triggerChange(formieCheckbox)
            })

            formieCheckbox.addEventListener('change', () => {
                customCheckbox.checked = formieCheckbox.checked
            })
        })
}

export const initRequestCustomFields = (scope = document) => {
    initRequestPageTabs(scope)
    initRequestAlertTimeout(scope)
    initRequestFieldErrors(scope)

    initCheckboxList({
        scope,
        fieldSelector: '[data-custom-cat-field]',
        readyKey: 'customCatReady',
        searchSelector: '[data-custom-cat-search]',
        optionSelector: '[data-custom-cat-option]',
        optionTitleKey: 'customCatTitle',
        checkboxSelector: '[data-custom-cat-checkbox]',
        badgeSelector: '[data-page0-items-selected]',
        clearSelector: '[data-page0-items-clear]'
    })

    initCheckboxList({
        scope,
        fieldSelector: '[data-custom-artist-field]',
        readyKey: 'customArtistReady',
        searchSelector: '[data-custom-artist-search]',
        optionSelector: '[data-custom-artist-option]',
        optionTitleKey: 'customArtistTitle',
        checkboxSelector: '[data-custom-artist-checkbox]',
        badgeSelector: '[data-page1-items-selected]',
        clearSelector: '[data-page1-items-clear]'
    })

    initRadioCards({
        scope,
        fieldSelector: '[data-custom-state-field]',
        readyKey: 'customStateReady',
        radioSelector: '[data-custom-state-radio]'
    })

    initRadioCards({
        scope,
        fieldSelector: '[data-custom-origin-field]',
        readyKey: 'customOriginReady',
        radioSelector: '[data-custom-origin-radio]'
    })

    initSellToggle(scope)
}
